// =====================================================================
// tpCustomer — Authentication
//
// Cookie-based auth: the JWT lives in an HttpOnly cookie set by the
// backend on /apicrm/auth/login. JavaScript never reads the token, so
// XSS can no longer exfiltrate it.
//
// Session bootstrap:
//   - On mount we call /apicrm/auth/me. The browser sends the cookie
//     (credentials: 'same-origin'); a 200 means we have a valid session.
//   - 401 → render <LoginPage>.
//   - LoginPage posts {email, password, totp}; on success the backend
//     sets the cookie and returns { user }.
//
// Exports (via window):
//   window.RealAuthProvider, window.LoginPage, window.tpHandleAuthLoss
// =====================================================================

(function () {
  const { useState, useEffect, useMemo, useCallback } = React;
  const AuthContext = window.AuthContext; // defined in ui.jsx

  // ── LoginPage ──────────────────────────────────────────────────────
  function LoginPage({ onSignedIn }) {
    const [email,    setEmail]    = useState('');
    const [password, setPassword] = useState('');
    const [totp,     setTotp]     = useState('');
    const [busy,     setBusy]     = useState(false);
    const [error,    setError]    = useState(null);

    const onSubmit = useCallback(async (e) => {
      e.preventDefault();
      if (busy) return;
      setBusy(true);
      setError(null);
      try {
        const payload = await tpAPI.request('/apicrm/auth/login', {
          method: 'POST',
          body:   { email, password, totp },
        });
        if (!payload || !payload.user) throw new Error('Malformed login response');
        onSignedIn(payload.user);
      } catch (err) {
        setError(err.message || 'Login failed');
      } finally {
        setBusy(false);
      }
    }, [busy, email, password, totp, onSignedIn]);

    return (
      <div style={styles.page}>
        <form style={styles.card} onSubmit={onSubmit} autoComplete="on">
          <img src="logo.png" alt="crmCustomer" style={styles.logo} />
          <div style={styles.title}>Sign in to crmCustomer</div>
          <div style={styles.subtitle}>Enter your email, password and 6-digit code</div>

          <label style={styles.label}>
            Email
            <input
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              disabled={busy}
              autoFocus
            />
          </label>

          <label style={styles.label}>
            Password
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              disabled={busy}
            />
          </label>

          <label style={styles.label}>
            6-digit code
            <input
              type="text"
              required
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              autoComplete="one-time-code"
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              style={{ ...styles.input, letterSpacing: 4, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
              disabled={busy}
              placeholder="123456"
            />
          </label>

          {error && <div style={styles.error}>{error}</div>}

          <button type="submit" disabled={busy} style={styles.button}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          <div style={styles.fineprint}>
            Password and 6-digit code are verified together — if either is wrong, the response is the same.
          </div>
        </form>
      </div>
    );
  }

  // ── RealAuthProvider ───────────────────────────────────────────────
  function RealAuthProvider({ children }) {
    // 'unknown' until /me resolves so we don't flash the LoginPage while
    // the session check is in flight.
    const [user, setUser]   = useState(null);
    const [state, setState] = useState('checking'); // 'checking' | 'ready'

    useEffect(function () {
      let cancelled = false;
      tpAPI.request('/apicrm/auth/me')
        .then(function (me) {
          if (cancelled) return;
          setUser(me);
          setState('ready');
        })
        .catch(function () {
          if (cancelled) return;
          setUser(null);
          setState('ready');
        });
      return function () { cancelled = true; };
    }, []);

    const signIn = useCallback(function (u) { setUser(u); }, []);

    const signOut = useCallback(async function () {
      try { await tpAPI.request('/apicrm/auth/logout', { method: 'POST' }); }
      catch (e) { /* ignore — even on failure we drop local state */ }
      setUser(null);
    }, []);

    // Called by api.js on any 401 — drops the local user without a hard
    // reload so unsaved input on unrelated panels is preserved.
    useEffect(function () {
      const handler = function () { setUser(null); };
      window.tpHandleAuthLoss = handler;
      return function () {
        // Only clear the global if it is still ours — identity check,
        // not a brittle sniff of the function's source text.
        if (window.tpHandleAuthLoss === handler) delete window.tpHandleAuthLoss;
      };
    }, []);

    const ctx = useMemo(function () { return { user, signIn, signOut }; }, [user, signIn, signOut]);

    if (state === 'checking') {
      return React.createElement('div', { style: styles.checking }, '');
    }
    if (!user) {
      return React.createElement(LoginPage, { onSignedIn: signIn });
    }
    return React.createElement(AuthContext.Provider, { value: ctx }, children);
  }

  // Uses the same design tokens as styles.css (loaded before the SPA),
  // so the login screen matches the app and follows light / dark theme.
  const styles = {
    page: {
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)', fontFamily: 'var(--sans)',
    },
    checking: {
      minHeight: '100vh', background: 'var(--bg)',
    },
    card: {
      width: 360, padding: 28, borderRadius: 'var(--radius-lg)',
      background: 'var(--bg-elev)',
      border: '1px solid var(--border)',
      boxShadow: 'var(--shadow-lg)',
      display: 'flex', flexDirection: 'column', gap: 14,
    },
    logo:     { height: 36, width: 'auto', alignSelf: 'center', marginBottom: 4 },
    title:    { fontSize: 18, fontWeight: 600, color: 'var(--text)', textAlign: 'center' },
    subtitle: { fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', marginBottom: 8 },
    label:    { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 },
    input:    {
      padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border-strong)',
      fontSize: 14, background: 'var(--bg-elev)', color: 'var(--text)',
    },
    button:   {
      marginTop: 8, padding: '10px 14px', borderRadius: 6, border: 'none',
      background: 'var(--accent)', color: '#1c1917',
      fontSize: 14, fontWeight: 600, cursor: 'pointer',
    },
    error:    { color: 'var(--red)', fontSize: 13, textAlign: 'center' },
    fineprint:{ fontSize: 11, color: 'var(--text-subtle)', textAlign: 'center', marginTop: 4 },
  };

  Object.assign(window, { RealAuthProvider, LoginPage });
}());
